<template>
  <div>
    <a-card style="margin-bottom:16px">
      <a-row :gutter="8" align="middle">
        <a-col>
          <a-input
            v-model:value="keyword"
            placeholder="搜索微信昵称或微信号"
            allow-clear
            style="width:240px"
            @press-enter="reloadFromFirstPage"
          />
        </a-col>
        <a-col>
          <a-select v-model:value="status" style="width:120px" @change="reloadFromFirstPage">
            <a-select-option value="all">全部状态</a-select-option>
            <a-select-option value="active">正常</a-select-option>
            <a-select-option value="disabled">已停用</a-select-option>
          </a-select>
        </a-col>
        <a-col><a-button @click="reloadFromFirstPage">查询</a-button></a-col>
        <a-col><a-button @click="resetFilters">重置</a-button></a-col>
        <a-col><a-button type="primary" @click="openCreate">新增用户</a-button></a-col>
        <a-col flex="auto" />
        <a-col>
          <a-button type="link" danger @click="openUnassigned">
            当前未归属节点 {{ unassignedProxyCount }} 个
          </a-button>
        </a-col>
      </a-row>
    </a-card>

    <a-table
      :columns="columns"
      :data-source="users"
      row-key="id"
      :loading="loading"
      :pagination="pagination"
      @change="onTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'identity'">
          <a-button type="link" style="padding:0" @click="openDetail(record)">{{ record.wechat_nickname }}</a-button>
          <div style="color:#6b7280;font-size:12px">{{ record.wechat_id || '未填写微信号' }}</div>
        </template>
        <template v-else-if="column.key === 'status'">
          <a-tag :color="record.status === 'active' ? 'green' : 'default'">
            {{ record.status === 'active' ? '正常' : '已停用' }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'proxy_count'">
          <span>{{ record.proxy_count }}</span>
          <span style="color:#6b7280">（正常 {{ record.active_proxy_count }}）</span>
        </template>
        <template v-else-if="column.key === 'nearest_expire_at'">
          {{ record.nearest_expire_at || '—' }}
        </template>
        <template v-else-if="column.key === 'traffic'">
          <div>↑ {{ formatBytes(record.bytes_up) }}</div>
          <div>↓ {{ formatBytes(record.bytes_down) }}</div>
        </template>
        <template v-else-if="column.key === 'action'">
          <a-space>
            <a-button type="link" size="small" @click="openDetail(record)">详情</a-button>
            <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
            <a-button
              v-if="record.status === 'active'"
              type="link"
              size="small"
              @click="confirmStatus(record, 'disabled')"
            >停用</a-button>
            <a-button
              v-else
              type="link"
              size="small"
              @click="confirmStatus(record, 'active')"
            >恢复</a-button>
            <a-button type="link" size="small" danger @click="confirmDelete(record)">删除</a-button>
          </a-space>
        </template>
      </template>
    </a-table>

    <a-modal
      v-model:open="editorVisible"
      :title="editingUser ? '编辑用户' : '新增用户'"
      :ok-text="editingUser ? '保存' : '创建'"
      cancel-text="取消"
      :confirm-loading="saving"
      :mask-closable="false"
      @ok="saveUser"
    >
      <a-form layout="vertical">
        <a-form-item label="微信昵称" required>
          <a-input v-model:value="editorForm.wechatNickname" :maxlength="64" placeholder="请输入微信昵称" />
        </a-form-item>
        <a-form-item label="微信号">
          <a-input v-model:value="editorForm.wechatID" :maxlength="64" placeholder="选填" />
        </a-form-item>
      </a-form>
    </a-modal>

    <ProxyUserDetailDrawer
      v-model:visible="detailVisible"
      :user="selectedUser"
      @select-proxy="openProxyDetail"
    />
    <ProxyDetailDrawer v-model:visible="proxyDetailVisible" :proxy="selectedProxy" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import {
  createProxyUser,
  deleteProxyUser,
  listProxyUsers,
  patchProxyUser,
} from '../api/proxyUsers';
import ProxyDetailDrawer from '../components/ProxyDetailDrawer.vue';
import ProxyUserDetailDrawer from '../components/ProxyUserDetailDrawer.vue';
import type { ProxyItem, ProxyUser, ProxyUserStatus } from '../types';
import { formatTrafficBytes } from '../utils/trafficFormat';

const router = useRouter();
const users = ref<ProxyUser[]>([]);
const loading = ref(false);
const keyword = ref('');
const status = ref<ProxyUserStatus | 'all'>('all');
const page = ref(1);
const pageSize = ref(20);
const total = ref(0);
const unassignedProxyCount = ref(0);
const editorVisible = ref(false);
const saving = ref(false);
const editingUser = ref<ProxyUser | null>(null);
const editorForm = reactive({ wechatNickname: '', wechatID: '' });
const detailVisible = ref(false);
const selectedUser = ref<ProxyUser | null>(null);
const proxyDetailVisible = ref(false);
const selectedProxy = ref<ProxyItem | null>(null);

const columns = [
  { title: '用户', key: 'identity', width: 220 },
  { title: '状态', key: 'status', width: 90 },
  { title: '节点数量', key: 'proxy_count', width: 160 },
  { title: '最近到期', key: 'nearest_expire_at', width: 180 },
  { title: '流量', key: 'traffic', width: 180 },
  { title: '操作', key: 'action', width: 300 },
];

const pagination = computed(() => ({
  current: page.value,
  pageSize: pageSize.value,
  total: total.value,
  showSizeChanger: true,
  pageSizeOptions: ['20', '50', '100'],
  showTotal: (value: number) => `共 ${value} 个用户`,
}));

const load = async () => {
  loading.value = true;
  try {
    const response = await listProxyUsers({
      keyword: keyword.value.trim() || undefined,
      status: status.value,
      limit: pageSize.value,
      offset: (page.value - 1) * pageSize.value,
    });
    users.value = response.data.items;
    total.value = response.data.total;
    unassignedProxyCount.value = response.data.unassigned_proxy_count;
  } catch (error: any) {
    message.error(userErrorMessage(error));
  } finally {
    loading.value = false;
  }
};

const reloadFromFirstPage = () => {
  page.value = 1;
  void load();
};

const resetFilters = () => {
  keyword.value = '';
  status.value = 'all';
  reloadFromFirstPage();
};

const onTableChange = (pager: any) => {
  page.value = pager.current;
  pageSize.value = pager.pageSize;
  void load();
};

const openCreate = () => {
  editingUser.value = null;
  editorForm.wechatNickname = '';
  editorForm.wechatID = '';
  editorVisible.value = true;
};

const openEdit = (user: ProxyUser) => {
  editingUser.value = user;
  editorForm.wechatNickname = user.wechat_nickname;
  editorForm.wechatID = user.wechat_id || '';
  editorVisible.value = true;
};

const saveUser = async () => {
  const nickname = editorForm.wechatNickname.trim();
  if (!nickname) {
    message.error('请填写微信昵称');
    return;
  }
  saving.value = true;
  try {
    if (editingUser.value) {
      await patchProxyUser(editingUser.value.id, {
        wechat_nickname: nickname,
        wechat_id: editorForm.wechatID.trim(),
      });
      message.success('用户已更新');
    } else {
      await createProxyUser({
        wechat_nickname: nickname,
        wechat_id: editorForm.wechatID.trim() || undefined,
      });
      message.success('用户已创建');
    }
    editorVisible.value = false;
    await load();
  } catch (error: any) {
    message.error(userErrorMessage(error));
  } finally {
    saving.value = false;
  }
};

const confirmStatus = (user: ProxyUser, nextStatus: ProxyUserStatus) => {
  Modal.confirm({
    title: nextStatus === 'disabled' ? '确认停用该用户？' : '确认恢复该用户？',
    content: nextStatus === 'disabled'
      ? '已有节点会继续运行，但该用户不能再接收新节点。'
      : '恢复后可用于新建和转移节点。',
    onOk: async () => {
      try {
        await patchProxyUser(user.id, { status: nextStatus });
        message.success(nextStatus === 'disabled' ? '用户已停用' : '用户已恢复');
        await load();
      } catch (error: any) {
        message.error(userErrorMessage(error));
      }
    },
  });
};

const confirmDelete = (user: ProxyUser) => {
  Modal.confirm({
    title: '确认删除该空用户？',
    content: '只有从未关联过节点的用户才能删除。',
    okType: 'danger',
    onOk: async () => {
      try {
        await deleteProxyUser(user.id);
        message.success('用户已删除');
        await load();
      } catch (error: any) {
        message.error(userErrorMessage(error));
      }
    },
  });
};

const openDetail = (user: ProxyUser) => {
  selectedUser.value = user;
  detailVisible.value = true;
};

const openProxyDetail = (proxy: ProxyItem) => {
  selectedProxy.value = proxy;
  proxyDetailVisible.value = true;
};

const openUnassigned = () => {
  void router.push({ path: '/proxies', query: { ownership: 'unassigned', status: 'all' } });
};

const userErrorMessage = (error: any) => {
  const code = error?.response?.data?.error?.code;
  if (code === 'WECHAT_ID_EXISTS') return '该微信号已存在';
  if (code === 'PROXY_USER_IN_USE') return '该用户存在当前或历史节点，请停用而不是删除';
  return error?.response?.data?.error?.message || '操作失败';
};

const formatBytes = formatTrafficBytes;

onMounted(() => void load());
</script>
